import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import {
  ICreateTenantRequest,
  IEditTenantRequest,
  ITenantUser,
  ITenant,
  ITenantDigitalPropertyLimitState,
  ITenantInfoWithPackageInfo,
  ITenantsResponse,
  ITenantSsoConfig,
  ITenantWithPackageId,
  ITenantDigitalPropertyUserWaySiteState,
} from '@shared/interfaces/tenant.interface';
import { IWorkspaceWithDpCount } from '@shared/interfaces/workspace.interface';
import { IScannableDocumentPdfPagesTestStateResponse } from '@shared/interfaces/scannable-document.interface';
import { IAcademySsoLink, ISkilljarSignupField } from '@shared/interfaces/academy-integration.interface';
import { AcademyUserRole } from '@shared/constants/academy-user';
import { ISalesforceAccount } from '@shared/interfaces/salesforce.interface';
import { ITenantMetrics } from '@shared/interfaces/tenant-metrics.interface';
import { AccessibilityAuditToolNames } from '@shared/constants/audit-tool';
import {
  IGetDigitalPropertyMetricsSnapshotsQuery,
  IPaginatedDigitalPropertyMetricsList,
} from '@shared/interfaces/digital-property-metrics.interface';
import { $tenant } from '@shared/constants/tenant';
import { IOrganizationReportResponse } from '@shared/interfaces/async-report.interface';
import { FileExtension } from '@shared/constants/file-extension';
import { DashboardDataFrequency } from '@shared/interfaces/ws-dashboard.interface';
import {
  IUserWayAccountCode,
  IUserWaySSOLink,
  IUserWayTenantIntegration,
  UserWayOrganizationAuthRequestTarget,
} from '@shared/interfaces/userway-integration.interface';
import { IDigitalPropertiesListResponse } from '@shared/interfaces/digital-property.interface';
import { ITenantFeatureFlagsResponse } from '@shared/interfaces/tenant-feature-flag.interface';
import { FeatureFlagCollection } from '@shared/interfaces/feature-flag.interface';
import { SalesforceApi } from './rest/salesforce.api';
import { AcademyApi } from './rest/academy.api';
import { TenantApi } from './rest/tenant.api';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly tenantFeatureFlagCache: Map<FeatureFlagCollection, Observable<boolean>> = new Map();

  public constructor(
    private tenantApi: TenantApi,
    private academyApi: AcademyApi,
    private salesforceApi: SalesforceApi,
  ) {}

  public getTenant(tenantId: string): Observable<ITenantWithPackageId> {
    return this.tenantApi.getTenant(tenantId);
  }

  public getTenantFeatureFlags(keys: FeatureFlagCollection[]): Observable<ITenantFeatureFlagsResponse> {
    return this.tenantApi.getTenantFeatureFlags(keys);
  }

  public getTenantFeatureFlag(key: FeatureFlagCollection): Observable<boolean> {
    if (this.tenantFeatureFlagCache.has(key) === false) {
      const flag$: Observable<boolean> = this.getTenantFeatureFlags([key]).pipe(
        map((response: ITenantFeatureFlagsResponse) => response.flags[key] === true),
        catchError((error: unknown) => {
          this.tenantFeatureFlagCache.delete(key);
          throw error;
        }),
        shareReplay(1),
      );
      this.tenantFeatureFlagCache.set(key, flag$);
    }

    return this.tenantFeatureFlagCache.get(key);
  }

  public getAllDigitalAssets(tenantId: string): Observable<IDigitalPropertiesListResponse> {
    return this.tenantApi.getAllDigitalAssets(tenantId);
  }

  public getCurrentTenant(): Observable<ITenantInfoWithPackageInfo> {
    return this.tenantApi.getCurrentTenant();
  }

  public getTenantFromTenantedScope(tenantId: string): Observable<ITenantInfoWithPackageInfo> {
    return this.tenantApi.getTenantFromTenantedScope(tenantId);
  }

  public getUserTenantsFromTenantedScope(): Observable<ITenant[]> {
    return this.tenantApi.getUserTenantsFromTenantedScope();
  }

  public updateTenant(tenantId: string, updateRequest: IEditTenantRequest): Observable<ITenant> {
    return this.tenantApi.updateTenant(tenantId, updateRequest);
  }

  public getWorkspaces(tenantId: string, searchString?: string): Observable<IWorkspaceWithDpCount[]> {
    return this.tenantApi.getWorkspaces(tenantId, searchString);
  }

  public getTenants(
    page: number = 0,
    limit: number = 0,
    term: string = '',
    sortBy?: string,
    direction?: string,
  ): Observable<ITenantsResponse> {
    return this.tenantApi.getTenants(page, limit, term, sortBy, direction);
  }

  public getMyTenants(
    page: number = 0,
    limit: number = 0,
    term: string = '',
    sortBy?: string,
    direction?: string,
  ): Observable<ITenantsResponse> {
    return this.tenantApi.getMyTenants(page, limit, term, sortBy, direction);
  }

  public createTenant(createRequest: ICreateTenantRequest): Observable<ITenant> {
    return this.tenantApi.createTenant(createRequest);
  }

  public setSsoConfig(tenantId: string, ssoConfig: ITenantSsoConfig): Observable<void> {
    return this.tenantApi.setSsoConfig(tenantId, ssoConfig);
  }

  public removeSsoConfig(tenantId: string, reason: string): Observable<void> {
    return this.tenantApi.removeSsoConfig(tenantId, reason);
  }

  public decommissionTenant(tenantId: string): Observable<void> {
    return this.tenantApi.decommissionTenant(tenantId);
  }

  public suspendTenant(tenantId: string): Observable<ITenant> {
    return this.tenantApi.suspendTenant(tenantId);
  }

  public resumeTenant(tenantId: string): Observable<ITenant> {
    return this.tenantApi.resumeTenant(tenantId);
  }

  public getPdfPagesTestState(tenantId: string): Observable<IScannableDocumentPdfPagesTestStateResponse> {
    return this.tenantApi.getPdfPagesTestState(tenantId);
  }

  public getTenantUsers(tenantId: string): Observable<ITenantUser[]> {
    return this.tenantApi.getTenantUsers(tenantId);
  }

  public activateUserInAcademy(tenantId: string, userId: string): Observable<void> {
    return this.tenantApi.activateUserInAcademy(tenantId, userId);
  }

  public deactivateUserInAcademy(tenantId: string, userId: string): Observable<void> {
    return this.tenantApi.deactivateUserInAcademy(tenantId, userId);
  }

  public setUserRolesInAcademy(tenantId: string, userId: string, roles: AcademyUserRole[]): Observable<void> {
    return this.tenantApi.setUserRolesInAcademy(tenantId, userId, roles);
  }

  public isAcademyAvailable(): Observable<boolean> {
    return this.academyApi.isAcademyAvailable();
  }

  public generateAcademySSOLink(): Observable<IAcademySsoLink> {
    return this.academyApi.generateAcademySSOLink();
  }

  public getAvailableSignupFields(): Observable<ISkilljarSignupField> {
    return this.academyApi.getAvailableSignupFields();
  }

  public getSalesforceAccounts(): Observable<ISalesforceAccount[]> {
    return this.salesforceApi.getSalesforceAccounts();
  }

  public getDigitalPropertyLimitState(tenantId: string): Observable<ITenantDigitalPropertyLimitState | null> {
    return this.tenantApi.getDigitalPropertyLimitState(tenantId);
  }

  public getDigitalPropertyUserWaySiteLimitState(tenantId?: string): Observable<ITenantDigitalPropertyUserWaySiteState | null> {
    return this.tenantApi.getDigitalPropertyUserWaySiteLimitState(tenantId);
  }

  public getTenantMetrics(
    tenantId: string,
    toolName: AccessibilityAuditToolNames,
    frequency: DashboardDataFrequency,
  ): Observable<ITenantMetrics[]> {
    return this.tenantApi.getTenantMetrics(tenantId, toolName, frequency);
  }

  public getDigitalPropertyMetricsSnapshots(
    tenantId: string,
    params: IGetDigitalPropertyMetricsSnapshotsQuery,
  ): Observable<IPaginatedDigitalPropertyMetricsList> {
    return this.tenantApi.getDigitalPropertyMetricsSnapshots(tenantId, params);
  }

  /**
   * Returns the entitlement token for the current tenant. If the tenant does not have an entitlement token
   * then one will be created. Only works in tenant environments.
   */
  public getOrCreateTenantEntitlementToken(): Observable<Pick<ITenant, $tenant.cloudsmithToken>> {
    return this.tenantApi.getOrCreateTenantEntitlementToken();
  }

  public generateScanningAgentEncryptionKey(): Observable<string> {
    return this.tenantApi.generateScanningAgentEncryptionKey();
  }

  /**
   * Returns the entitlement token for the current tenant. Only works in tenant environments.
   * Entitlement token is possibly undefined.
   */
  public getTenantEntitlementToken(): Observable<Pick<ITenant, $tenant.cloudsmithToken>> {
    return this.tenantApi.getTenantEntitlementToken();
  }

  public rotateTenantEntitlementToken(): Observable<Pick<ITenant, $tenant.cloudsmithToken>> {
    return this.tenantApi.rotateTenantEntitlementToken();
  }

  public getOrCreateTenantDockerToken(): Observable<Pick<ITenant, $tenant.dockerCloudsmithToken>> {
    return this.tenantApi.getOrCreateTenantDockerToken();
  }

  public downloadOrganizationReport(
    tenantId: string,
    toolName: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
  ): Observable<IOrganizationReportResponse> {
    return this.tenantApi.downloadOrganizationReport(tenantId, toolName, fileExtension);
  }

  public generateOrganizationReport(
    tenantId: string,
    toolName: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
  ): Observable<IOrganizationReportResponse> {
    return this.tenantApi.generateOrganizationReport(tenantId, toolName, fileExtension);
  }
  // GET /tenants/current/userway-sso-link
  public generateUserWaySSOLink(
    target: UserWayOrganizationAuthRequestTarget,
    userwayAccountCode?: string,
  ): Observable<IUserWaySSOLink> {
    return this.tenantApi.generateUserWaySSOLink(target, userwayAccountCode);
  }

  public generateUserWaySSORedirectUrl(
    target: UserWayOrganizationAuthRequestTarget,
    userwayAccountCode?: string,
    additionalQueryParams?: URLSearchParams,
  ): string {
    return this.tenantApi.generateUserWaySSORedirectUrl(target, userwayAccountCode, additionalQueryParams);
  }

  // GET /tenants/current/userway-account-code
  public getUserWayAccountCode(): Observable<IUserWayAccountCode> {
    return this.tenantApi.getUserWayAccountCode();
  }

  // GET /tenants/current/userway
  public getUserWayAccess(): Observable<IUserWayTenantIntegration> {
    return this.tenantApi.getUserWayAccess();
  }
}
